export interface PageLeaveWatch {
  /** The member started leaving the page while `request` was in flight. */
  readonly leaving: boolean;
}

/**
 * Watch for the member leaving the page while `request` is in flight.
 *
 * @remarks A browser drops a request in flight when the page is left, and rejects it like a real
 * network failure — and no React cleanup runs, so the request's own signal never aborts. When the
 * rejection lands differs by engine: WebKit and Firefox reject after `beforeunload` but BEFORE
 * `pagehide`; Chromium after `pagehide`. Hence both events, and a flag rather than an abort:
 * Chromium keeps a load running while the page sits in the back/forward cache and delivers it on
 * restore, which an abort would destroy.
 *
 * The listeners last only while `request` is in flight, because Firefox will not put a page with a
 * `beforeunload` listener in the back/forward cache.
 */
export function watchPageLeave(request: Promise<unknown>): PageLeaveWatch {
  const listeners = new AbortController();
  let leaving = false;
  const leave = (): void => {
    leaving = true;
  };
  window.addEventListener('beforeunload', leave, { signal: listeners.signal });
  window.addEventListener('pagehide', leave, { signal: listeners.signal });
  window.addEventListener(
    'pageshow',
    (event) => {
      if (event.persisted) leaving = false;
    },
    { signal: listeners.signal },
  );
  const stop = (): void => {
    listeners.abort();
  };
  request.then(stop, stop);
  return {
    get leaving() {
      return leaving;
    },
  };
}
